import { useEffect, useRef } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

/**
 * Applies CSS classes to filtered column headers for visual indication
 */
export const applyFilterClasses = (
	filteredColumns: Set<string>,
	processedColumns: BoardColumn[],
	rafIdRef: { current: number | undefined },
) => {
	const grid = document.querySelector(".quote-board-grid");
	if (!grid) {
		rafIdRef.current = requestAnimationFrame(() =>
			applyFilterClasses(filteredColumns, processedColumns, rafIdRef),
		);
		return;
	}

	const headers = grid.querySelectorAll(".k-table-th");
	const filteredFieldNames = new Set(filteredColumns);

	for (const header of headers) {
		const columnTitle = header
			.querySelector(".k-column-title")
			?.textContent?.trim();
		if (!columnTitle) continue;

		const fieldName = processedColumns.find(
			(col) => col.title === columnTitle,
		)?.field;
		if (!fieldName) continue;

		const shouldBeFiltered = filteredFieldNames.has(fieldName);
		const currentlyFiltered = header.classList.contains("custom-filtered");

		if (shouldBeFiltered && !currentlyFiltered) {
			header.classList.add("custom-filtered");
		} else if (!shouldBeFiltered && currentlyFiltered) {
			header.classList.remove("custom-filtered");
		}
	}

	if (filteredColumns.size > 0) {
		rafIdRef.current = requestAnimationFrame(() =>
			applyFilterClasses(filteredColumns, processedColumns, rafIdRef),
		);
	}
};

/**
 * Force cleanup of all filter classes
 */
export const forceCleanupFilterClasses = () => {
	const headers = document.querySelectorAll(".k-table-th.custom-filtered");
	for (const header of headers) {
		header.classList.remove("custom-filtered");
	}
};

/**
 * Schedule cleanup of filter classes (legacy function for test compatibility)
 */
export const scheduleCleanup = (
	cleanupScheduledRef: { current: boolean },
	rafIdRef: { current: number | undefined },
) => {
	if (cleanupScheduledRef.current) return;
	cleanupScheduledRef.current = true;

	rafIdRef.current = requestAnimationFrame(() => {
		const headers = document.querySelectorAll(".k-table-th.custom-filtered");
		for (const header of headers) {
			header.classList.remove("custom-filtered");
		}
		cleanupScheduledRef.current = false;
	});
};

/**
 * Hook to manage filter CSS classes and DOM observer for filter state
 */
export const useFilterCssManagement = (
	filteredColumns: Set<string>,
	processedColumns: BoardColumn[],
) => {
	const previousFilterSizeRef = useRef(filteredColumns.size);

	useEffect(() => {
		let rafId: number | undefined;
		let observer: MutationObserver | undefined;
		const rafIdRef = { current: rafId };

		const applyFilterClassesWrapper = () => {
			applyFilterClasses(filteredColumns, processedColumns, rafIdRef);
			rafId = rafIdRef.current;
		};

		const setupMutationObserver = () => {
			const gridElement = document.querySelector(".quote-board-grid");
			if (gridElement && !observer) {
				observer = new MutationObserver(() => {
					if (filteredColumns.size === 0) {
						const orphanedHeaders = document.querySelectorAll(
							".k-table-th.custom-filtered",
						);
						for (const header of orphanedHeaders) {
							header.classList.remove("custom-filtered");
						}
					}
				});

				observer.observe(gridElement, {
					childList: true,
					subtree: true,
					attributes: true,
					attributeFilter: ["class"],
				});
			}
		};

		rafId =
			filteredColumns.size > 0
				? requestAnimationFrame(() => {
						applyFilterClassesWrapper();
						setupMutationObserver();
					})
				: requestAnimationFrame(() => {
						applyFilterClasses(new Set(), processedColumns, {
							current: undefined,
						});
						forceCleanupFilterClasses();
						setupMutationObserver();
					});

		previousFilterSizeRef.current = filteredColumns.size;

		return () => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
			if (observer) {
				observer.disconnect();
			}
		};
	}, [filteredColumns, processedColumns]);
};
